import classNames from "classnames";
import { ButtonHTMLAttributes, forwardRef } from "react";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-close-button.scss";

/** `md` — 24px box, 16px glyph: modal, drawer and popover headers · `sm` — 20px box, 14px glyph: a toast, a field's clear. */
export type KuikaCloseButtonSize = "sm" | "md";

export interface IKuikaCloseButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "type"> {
  size?: KuikaCloseButtonSize;
}

const GLYPH_SIZE: Record<KuikaCloseButtonSize, number> = { sm: 14, md: 16 };

/**
 * The one dismiss control of the designer: an ✕ glyph in a small box that stays visually quiet but
 * answers a 32px pointer target (`--hit-min`), shows the hover surface and the keyboard focus ring.
 *
 * - `aria-label` defaults to "Close"; pass another for a clear/remove action.
 * - Forwards its ref and every other button attribute (`data-testid`, `onClick`, `disabled`…), so it
 *   works as the child of Radix `Dialog.Close asChild` / `Popover.Close asChild`.
 * - Always `type="button"`: inside a form it never submits.
 */
const KuikaCloseButton = forwardRef<HTMLButtonElement, IKuikaCloseButtonProps>(
  ({ size = "md", className, "aria-label": ariaLabel = "Close", ...rest }, ref): JSX.Element => (
    <button
      {...rest}
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      className={classNames("kuika-close-button", `kuika-close-button--${size}`, className)}
    >
      <KuikaSvg name="x" width={GLYPH_SIZE[size]} height={GLYPH_SIZE[size]} aria-hidden="true" />
    </button>
  )
);

KuikaCloseButton.displayName = "KuikaCloseButton";

export default KuikaCloseButton;
